import React from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';

/**
 * Read-only view of a compiled form: the frozen payer/recipient snapshot plus
 * its box and state detail rows. Nothing here is editable -- correcting a
 * filed return means regenerating with a new correction_seq, not patching
 * this record.
 */

interface BoxDetail {
  box_code: string;
  amount: string;
  threshold_applied?: string | null;
}

interface StateDetail {
  state_code: string;
  state_income?: string | null;
  state_tax_withheld: string;
}

interface FormDetail {
  id: number;
  tax_year: number;
  form_type: string;
  filing_status: number;
  total_reportable_amount: string;
  federal_tax_withheld: string;

  payer_legal_name: string;
  payer_dba_name?: string | null;
  payer_masked_ein?: string | null;
  payer_address_line1?: string | null;
  payer_address_line2?: string | null;
  payer_city?: string | null;
  payer_state?: string | null;
  payer_postal_code?: string | null;

  recipient_legal_name: string;
  recipient_business_name_dba?: string | null;
  recipient_masked_tin?: string | null;
  recipient_vendor_code?: string | null;
  recipient_address_line1?: string | null;
  recipient_address_line2?: string | null;
  recipient_city?: string | null;
  recipient_state?: string | null;
  recipient_postal_code?: string | null;
  recipient_is_backup_withholding: boolean;
  recipient_email?: string | null;
  recipient_edelivery_consent: boolean;
  furnished_at?: string | null;
  furnished_method?: number | null;
  filed_at?: string | null;
  correction_type: number;
  original_form_id?: number | null;

  box_details: BoxDetail[];
  state_details: StateDetail[];
}

const EMPTY_VALUE = '--';

const FILING_STATUS_KEYS: Record<number, string> = {
  1: 'status.draft',
  2: 'status.calculated',
  3: 'status.ready_for_review',
  4: 'status.approved',
  5: 'status.void',
};

const FILING_STATUS_BADGE_VARIANT: Record<number, string> = {
  1: 'secondary',
  2: 'secondary',
  3: 'info',
  4: 'success',
  5: 'danger',
};

interface Props {
  show: boolean;
  form: FormDetail | null;
  saving: boolean;
  onClose: () => void;
  onDownloadPdf: () => void;
  onApprove: () => void;
  onUnapprove: () => void;
  onVoid: () => void;
  onFurnishEmail: () => void;
  onCorrect: (correctionType: 1 | 2) => void;
}

const FormDetailModal: React.FC<Props> = ({
  show, form, saving, onClose, onDownloadPdf, onApprove, onUnapprove, onVoid, onFurnishEmail,
  onCorrect,
}) => {
  const { t } = useTranslation('Tax1099Forms');

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {form ? t('detail.title', { formType: form.form_type, year: form.tax_year }) : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {form && (
          <div className="row">
            <div className="col-md-6 mb-3">
              <h6>{t('detail.payer')}</h6>
              <div>{form.payer_legal_name}</div>
              {form.payer_dba_name && <div className="text-muted">{form.payer_dba_name}</div>}
              <div>{t('detail.ein')}: {form.payer_masked_ein || EMPTY_VALUE}</div>
              <div>{form.payer_address_line1}</div>
              {form.payer_address_line2 && <div>{form.payer_address_line2}</div>}
              <div>
                {[form.payer_city, form.payer_state, form.payer_postal_code]
                  .filter(Boolean).join(', ')}
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <h6>{t('detail.recipient')}</h6>
              <div>{form.recipient_legal_name}</div>
              {form.recipient_business_name_dba && (
                <div className="text-muted">{form.recipient_business_name_dba}</div>
              )}
              <div>
                <code>{form.recipient_vendor_code}</code>
              </div>
              <div>{t('detail.tin')}: {form.recipient_masked_tin || EMPTY_VALUE}</div>
              <div>{form.recipient_address_line1}</div>
              {form.recipient_address_line2 && <div>{form.recipient_address_line2}</div>}
              <div>
                {[form.recipient_city, form.recipient_state, form.recipient_postal_code]
                  .filter(Boolean).join(', ')}
              </div>
              {form.recipient_is_backup_withholding && (
                <Badge bg="warning" text="dark" className="mt-1">
                  {t('detail.backup_withholding')}
                </Badge>
              )}
              {form.correction_type === 1 && (
                <Badge bg="danger" className="mt-1 ms-1">{t('detail.correction_type_1')}</Badge>
              )}
              {form.correction_type === 2 && (
                <Badge bg="danger" className="mt-1 ms-1">{t('detail.correction_type_2')}</Badge>
              )}
              {form.correction_type === 0 && form.original_form_id && (
                <Badge bg="secondary" className="mt-1 ms-1">
                  {t('detail.corrects_form', { id: form.original_form_id })}
                </Badge>
              )}
            </div>

            <div className="col-12 mb-3">
              <span className="me-3">
                {t('detail.status')}:{' '}
                <Badge bg={FILING_STATUS_BADGE_VARIANT[form.filing_status] || 'secondary'}>
                  {t(FILING_STATUS_KEYS[form.filing_status] || 'status.draft')}
                </Badge>
              </span>
              <span className="me-3">
                {t('detail.total')}: {Number(form.total_reportable_amount).toFixed(2)}
              </span>
              <span>
                {t('detail.federal_withheld')}: {Number(form.federal_tax_withheld).toFixed(2)}
              </span>
            </div>

            <div className="col-12 mb-3">
              {form.furnished_at ? (
                <Badge bg="success">{t('detail.furnished_on', { date: form.furnished_at })}</Badge>
              ) : (
                <Badge bg="secondary">{t('detail.not_furnished')}</Badge>
              )}
              {!form.recipient_edelivery_consent && (
                <span className="text-muted small ms-2">{t('detail.no_edelivery_consent')}</span>
              )}
              {form.recipient_edelivery_consent && !form.recipient_email && (
                <span className="text-muted small ms-2">{t('detail.no_recipient_email')}</span>
              )}
            </div>

            <div className="col-md-6 mb-3">
              <h6>{t('detail.boxes')}</h6>
              {form.box_details.length === 0 && (
                <p className="text-muted small">{t('detail.no_boxes')}</p>
              )}
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>{t('detail.box_code')}</th>
                    <th className="text-end">{t('detail.amount')}</th>
                    <th className="text-end">{t('detail.threshold')}</th>
                  </tr>
                </thead>
                <tbody>
                  {form.box_details.map((box) => (
                    <tr key={box.box_code}>
                      <td>{box.box_code}</td>
                      <td className="text-end">{Number(box.amount).toFixed(2)}</td>
                      <td className="text-end">
                        {box.threshold_applied != null
                          ? Number(box.threshold_applied).toFixed(2)
                          : EMPTY_VALUE}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="col-md-6 mb-3">
              <h6>{t('detail.states')}</h6>
              {form.state_details.length === 0 && (
                <p className="text-muted small">{t('detail.no_states')}</p>
              )}
              {form.state_details.length > 0 && (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{t('detail.state_code')}</th>
                      <th className="text-end">{t('detail.state_income')}</th>
                      <th className="text-end">{t('detail.state_withheld')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.state_details.map((state) => (
                      <tr key={state.state_code}>
                        <td>{state.state_code}</td>
                        <td className="text-end">
                          {state.state_income != null
                            ? Number(state.state_income).toFixed(2)
                            : EMPTY_VALUE}
                        </td>
                        <td className="text-end">
                          {Number(state.state_tax_withheld).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{t('detail.close')}</Button>
        {form?.filing_status === 1 && (
          <Button variant="success" onClick={onApprove} disabled={saving}>
            {t('detail.approve')}
          </Button>
        )}
        {form?.filing_status === 4 && (
          <Button variant="outline-secondary" onClick={onUnapprove} disabled={saving}>
            {t('detail.unapprove')}
          </Button>
        )}
        {form?.filing_status === 4 && !form.furnished_at && (
          <Button
            variant="info"
            onClick={onFurnishEmail}
            disabled={saving || !form.recipient_edelivery_consent || !form.recipient_email}
            title={
              !form.recipient_edelivery_consent
                ? t('detail.no_edelivery_consent')
                : !form.recipient_email ? t('detail.no_recipient_email') : undefined
            }
          >
            {t('detail.send_email')}
          </Button>
        )}
        {form && form.filing_status !== 5 && (
          <Button variant="outline-danger" onClick={onVoid} disabled={saving}>
            {t('detail.void')}
          </Button>
        )}
        {form && (form.furnished_at || form.filed_at) && (
          <>
            <Button
              variant="outline-danger"
              onClick={() => onCorrect(1)}
              disabled={saving}
              title={t('detail.correct_amount_help')}
            >
              {t('detail.correct_amount')}
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => onCorrect(2)}
              disabled={saving}
              title={t('detail.correct_identity_help')}
            >
              {t('detail.correct_identity')}
            </Button>
          </>
        )}
        <Button variant="primary" onClick={onDownloadPdf} disabled={!form}>
          {t('detail.download_pdf')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FormDetailModal;
